import type { ReactNode } from "react";
import { useContext } from "react";
import {
  XYChart as VXYChart,
  BarSeries as VBarSeries,
  LineSeries as VLineSeries,
  AreaSeries as VAreaSeries,
  GlyphSeries as VGlyphSeries,
  AnimatedBarSeries as VAnimatedBarSeries,
  AnimatedLineSeries as VAnimatedLineSeries,
  AnimatedAreaSeries as VAnimatedAreaSeries,
  AnimatedGlyphSeries as VAnimatedGlyphSeries,
  TooltipContext as VTooltipContext,
} from "@visx/xychart";
import type {
  AxisScale,
  SeriesProps,
  Margin,
  GlyphProps,
  TooltipContextType,
} from "@visx/xychart";
import type { CurveFactory, CurveFactoryLineOnly } from "@visx/vendor/d3-shape";
import type { FC } from "react";
import { usePrefersReducedMotion } from "../../hooks/use-prefers-reduced-motion";
import { useChartTheme } from "./theme";
import { useChartColors, type ChartColorToken } from "./colors";
import { omitUndefined } from "../../lib/props";
import type { Omittable } from "../../lib/props";

/** Underlying visx series are generic; bridge them through a permissive alias so
 * our wrappers can own a fully-typed, closed public prop surface. */
type AnyComp = (props: Record<string, unknown>) => ReactNode;

/* ------------------------------------------------------------------ XYChart */

export type XYChartProps<Datum extends object> = Omittable<{
  /** Fixed height in px. Omit to fill the (sized) parent — visx self-measures. */
  height?: number;
  /** Fixed width in px. Omit to fill the parent. */
  width?: number;
  /** Space reserved around the plotting area for axes/labels. */
  margin?: Margin;
  /** X-scale config, e.g. `{ type: "band", paddingInner: 0.3 }` or `{ type: "time" }`. */
  xScale: React.ComponentProps<typeof VXYChart>["xScale"];
  /** Y-scale config, e.g. `{ type: "linear" }`. */
  yScale: React.ComponentProps<typeof VXYChart>["yScale"];
  /** Render horizontally (swap x/y). */
  horizontal?: boolean | "auto";
  /** aria-label for the chart svg. */
  ariaLabel?: string;
  /** Fired for the nearest datum on pointer-up (e.g. open a drilldown). */
  onPointerUp?: (params: import("@visx/xychart").EventHandlerParams<Datum>) => void;
  onPointerMove?: (params: import("@visx/xychart").EventHandlerParams<Datum>) => void;
  onPointerOut?: React.ComponentProps<typeof VXYChart>["onPointerOut"];
  children: ReactNode;
}>;

/**
 * The themed XYChart scaffold — the easy path. Self-measuring (wraps visx's
 * `ParentSize` when `width`/`height` are omitted) and auto-injects the token
 * theme, so series color, grid, axes, and labels are on-brand with **zero color
 * props**. Compose `BarSeries`/`LineSeries`/`AreaSeries`/`GlyphSeries` (optionally
 * inside `BarStack`/`BarGroup`/`AreaStack`) with `Axis`/`Grid`/`Tooltip`.
 */
export function XYChart<Datum extends object>({
  children,
  ...props
}: XYChartProps<Datum>) {
  const theme = useChartTheme();
  return (
    <VXYChart theme={theme} {...omitUndefined(props)}>
      {children}
    </VXYChart>
  );
}

/* ------------------------------------------------------------- shared props */

type SeriesBase<Datum extends object> = SeriesProps<AxisScale, AxisScale, Datum> & Omittable<{
  /** Override the series color with a semantic token (defaults to the theme ramp by `dataKey`). */
  colorToken?: ChartColorToken;
}>;

function useSeriesColor(colorToken?: ChartColorToken): string | undefined {
  return useChartColors().resolve(colorToken);
}

/* ---------------------------------------------------------------- BarSeries */

export type BarSeriesProps<Datum extends object> = SeriesBase<Datum> &
  Omittable<{
  /** Per-datum color override; falls back to the theme color for the series. */
  colorAccessor?: (d: Datum, index: number) => string | null | undefined;
  /** Bar padding [0,1] when thickness isn't from a band scale. */
  barPadding?: number;
  radius?: number;
  radiusAll?: boolean;
  radiusTop?: boolean;
  radiusRight?: boolean;
  radiusBottom?: boolean;
  radiusLeft?: boolean;
}>;

export function BarSeries<Datum extends object>({
  colorToken,
  colorAccessor,
  ...rest
}: BarSeriesProps<Datum>) {
  const reduced = usePrefersReducedMotion();
  const color = useSeriesColor(colorToken);
  const Comp = (reduced ? VBarSeries : VAnimatedBarSeries) as AnyComp;
  return (
    <Comp {...rest} colorAccessor={color ? () => color : colorAccessor} />
  );
}

/* --------------------------------------------------------------- LineSeries */

export type LineSeriesProps<Datum extends object> = SeriesBase<Datum> &
  Omittable<{
  /** Curve factory (from chart-kit's `curve*` exports). Defaults to `curveLinear`. */
  curve?: CurveFactory | CurveFactoryLineOnly;
  /** Per-dataKey color override; falls back to the theme color for the series. */
  colorAccessor?: (dataKey: string) => string | null | undefined;
}>;

export function LineSeries<Datum extends object>({
  colorToken,
  colorAccessor,
  ...rest
}: LineSeriesProps<Datum>) {
  const reduced = usePrefersReducedMotion();
  const color = useSeriesColor(colorToken);
  const Comp = (reduced ? VLineSeries : VAnimatedLineSeries) as AnyComp;
  return (
    <Comp {...rest} colorAccessor={color ? () => color : colorAccessor} />
  );
}

/* --------------------------------------------------------------- AreaSeries */

export type AreaSeriesProps<Datum extends object> = SeriesBase<Datum> &
  Omittable<{
  curve?: CurveFactory | CurveFactoryLineOnly;
  /** Also stroke a line along the area's value edge. */
  renderLine?: boolean;
  /** Override the area baseline per datum (band charts). */
  y0Accessor?: (d: Datum) => import("@visx/scale").ScaleInput<AxisScale>;
  /** Per-dataKey color override; falls back to the theme color for the series. */
  colorAccessor?: (dataKey: string) => string | null | undefined;
}>;

export function AreaSeries<Datum extends object>({
  colorToken,
  colorAccessor,
  dataKey,
  ...rest
}: AreaSeriesProps<Datum>) {
  const reduced = usePrefersReducedMotion();
  const token = useSeriesColor(colorToken);
  const Comp = (reduced ? VAreaSeries : VAnimatedAreaSeries) as AnyComp;
  // The area is the one visx series that never consults `colorAccessor` — it
  // paints from `colorScale(dataKey)` and nothing else, and an unrecognised
  // accessor falls into its rest props and lands on the path element as a DOM
  // attribute. So the override is resolved here and applied directly: `fill`
  // for the band, `lineProps.stroke` for the value edge `renderLine` draws.
  // Both are spread after visx's own defaults, so they win.
  const color = token ?? colorAccessor?.(dataKey) ?? undefined;
  return (
    <Comp
      {...rest}
      dataKey={dataKey}
      {...(color ? { fill: color, lineProps: { stroke: color } } : null)}
    />
  );
}

/* -------------------------------------------------------------- GlyphSeries */

export type GlyphSeriesProps<Datum extends object> = SeriesBase<Datum> &
  Omittable<{
  /** Glyph size in px, or a per-datum function. */
  size?: number | ((d: Datum) => number);
  /** Per-datum color override; falls back to the theme color for the series. */
  colorAccessor?: (d: Datum, index: number) => string | null | undefined;
  /** Custom glyph renderer (defaults to a themed dot). */
  renderGlyph?: FC<GlyphProps<Datum>>;
}>;

export function GlyphSeries<Datum extends object>({
  colorToken,
  colorAccessor,
  ...rest
}: GlyphSeriesProps<Datum>) {
  const reduced = usePrefersReducedMotion();
  const color = useSeriesColor(colorToken);
  const Comp = (reduced ? VGlyphSeries : VAnimatedGlyphSeries) as AnyComp;
  return (
    <Comp {...rest} colorAccessor={color ? () => color : colorAccessor} />
  );
}

/* --------------------------------- context-themed chrome (re-exported as-is) */
// These read the injected theme from XYChart's context, so they are on-brand by
// default. Names are verbatim visx so a session recognizes them from pretraining.
// `Grid`/`Tooltip`/`ThemeProvider` are prefixed `Chart*` because the library
// already owns those names for its layout/UI/theme primitives — inside one
// namespace the chart versions must disambiguate. Everything else is verbatim visx.
export {
  BarStack,
  BarGroup,
  AreaStack,
  Axis,
  Grid as ChartGrid,
  Tooltip as ChartTooltip,
  Annotation,
  AnnotationLabel,
  AnnotationConnector,
  AnnotationCircleSubject,
  AnnotationLineSubject,
  DataProvider,
  DataContext,
  ThemeProvider as ChartThemeProvider,
  ThemeContext as ChartThemeContext,
  // Prefixed: the package barrel already owns TooltipProvider (the base
  // tooltip part). Same rule as ChartThemeProvider above.
  TooltipProvider as ChartTooltipProvider,
  TooltipContext,
  EventEmitterProvider,
  useEventEmitter,
} from "@visx/xychart";

export type {
  Margin,
  AxisScale,
  SeriesProps,
  GlyphProps,
  XYChartTheme,
  EventHandlerParams,
  TooltipData,
  TooltipContextType,
} from "@visx/xychart";

/**
 * Read the active XYChart tooltip state (visx ships the context but no hook).
 * Returns `null` outside an `<XYChart>`/`TooltipProvider`.
 */
export function useTooltipContext<
  Datum extends object,
>(): TooltipContextType<Datum> | null {
  return useContext(VTooltipContext) as TooltipContextType<Datum> | null;
}

/**
 * `tickLabelProps` for a crowded categorical **bottom** axis — the stage / type /
 * assignee name rows on bar charts. Angles each label instead of laying them flat,
 * so a narrow card's ticks stop colliding into an unreadable smear. Anchored at
 * its end so the text hangs down-left from the tick. Merges OVER the theme's tick
 * style, so the on-brand fill/size are kept; only the geometry changes. Pair it
 * with a taller bottom margin (~52px) so the angled text isn't clipped. Not for
 * time axes (their ticks are pre-sampled and already fit) — those stay flat.
 */
export const angledTickLabelProps = () =>
  ({
    angle: -35,
    textAnchor: "end",
    dx: "-0.25em",
    dy: "0.25em",
  }) as const;
